import { useEffect, useState } from "react";

import Box from "@/components/Box";
import {
  dashboardSidebarFiltersTitleFontSize,
  dashboardSidebarGap,
  dashboardSidebarPadding
} from "@/components/organisations/learningHub/dashboardLayout";
import { SHOP_FILTER_DESKTOP_MIN_WIDTH } from "@/components/organisations/shop/shopFilterBreakpoints";
import FilterIcon from "@/images/FilterIcon";
import Checkbox from "@/tpo/Checkbox";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Menu from "@/tpo/Menu";
import Stack from "@/tpo/Stack";
import { startCase } from "@/utils/nodash";
import { ActionIcon } from "@/v2/Buttons";
import ButtonV2 from "@/v2/Buttons";
import styled from "styled-components";
import { useTheme } from "styled-components";

const PILL_FILTER_GROUPS = new Set(["gender", "eventTypes"]);

/** Sticky top offset: nav sits 40px from viewport top and is 60px tall, plus layout spacing below. */
const DESKTOP_FILTER_STICKY_TOP = 180;
const DESKTOP_FILTER_MAX_HEIGHT = `calc(100vh - ${DESKTOP_FILTER_STICKY_TOP}px)`;

const ProductFiltersDesktopStack = styled(Stack)`
  display: none;

  @media screen and (min-width: ${SHOP_FILTER_DESKTOP_MIN_WIDTH}) {
    display: flex;
    position: sticky;
    top: ${DESKTOP_FILTER_STICKY_TOP}px;
    max-height: ${DESKTOP_FILTER_MAX_HEIGHT};
    overflow: hidden;
  }
`;

const ProductFiltersMobileWrap = styled(Box)`
  display: flex;

  @media screen and (min-width: ${SHOP_FILTER_DESKTOP_MIN_WIDTH}) {
    display: none;
  }
`;

const FILTER_GROUP_LABELS = {
  categories: "Health Areas",
  topics: "Topics",
  eventTypes: "Format"
};

function filterGroupLabel(groupKey) {
  return FILTER_GROUP_LABELS[groupKey] ?? startCase(groupKey);
}

function GenderFilterPills({ groupKey, values, validatedFilters, toggleFilter, getFilterLabel }) {
  return (
    <Group flexWrap="wrap" gap={4}>
      {values.map(val => {
        const selected = validatedFilters[groupKey]?.includes(val);
        return (
          <ButtonV2
            key={val}
            variant="pill"
            sx={{
              px: 20,
              py: "10px",
              height: "auto",
              fontSize: 10,
              color: selected ? "white" : "dark",
              bg: selected ? "green" : "transparent",
              borderStyle: "solid",
              borderWidth: selected ? 0 : 2,
              borderColor: "dark"
            }}
            onClick={() => toggleFilter(groupKey, val)}
            type="button"
          >
            {getFilterLabel ? getFilterLabel(groupKey, val) : val}
          </ButtonV2>
        );
      })}
    </Group>
  );
}

function FilterSectionLabel({ groupKey }) {
  return (
    <Box
      fontFamily="gilroyBold"
      color="dark"
      letterSpacing={2.8}
      fontSize={12}
      textTransform="uppercase"
    >
      {filterGroupLabel(groupKey)}
    </Box>
  );
}

function StaticPillFilterSection({
  groupKey,
  values,
  validatedFilters,
  toggleFilter,
  getFilterLabel
}) {
  return (
    <Stack gap={16}>
      <FilterSectionLabel groupKey={groupKey} />
      <GenderFilterPills
        groupKey={groupKey}
        values={values}
        validatedFilters={validatedFilters}
        toggleFilter={toggleFilter}
        getFilterLabel={getFilterLabel}
      />
    </Stack>
  );
}

const DEFAULT_EXPANDED_SECTIONS = new Set(["categories", "topics"]);

function CollapsibleFilterSection({
  groupKey,
  values,
  validatedFilters,
  toggleFilter,
  getFilterLabel
}) {
  const [open, setOpen] = useState(() => DEFAULT_EXPANDED_SECTIONS.has(groupKey));

  return (
    <Stack gap={16}>
      <Group
        justifyContent="space-between"
        alignItems="center"
        cursor="pointer"
        onClick={() => setOpen(isOpen => !isOpen)}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(isOpen => !isOpen);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <FilterSectionLabel groupKey={groupKey} />
        <ChevronComponent direction={open ? "top" : "bottom"} width={6} fill="dark" />
      </Group>
      {open && (
        <Stack gap={16}>
          {values.map(val => (
            <Checkbox
              key={val}
              label={getFilterLabel ? getFilterLabel(groupKey, val) : val}
              styles={{
                checkbox: {
                  size: "sm"
                }
              }}
              checked={validatedFilters[groupKey]?.includes(val)}
              onChange={() => toggleFilter(groupKey, val)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function ProductFiltersStack({
  allFilters,
  validatedFilters,
  toggleFilter,
  onReset,
  filtersTitle = "Filters",
  topContent = null,
  contentAfterTop = null,
  scrollFilterGroups = false,
  getFilterLabel
}) {
  const theme = useTheme();
  const filterGroups = Object.entries(allFilters);

  if (!filterGroups.length && !topContent && !contentAfterTop) {
    return null;
  }

  const headerBlock = (
    <Stack gap={dashboardSidebarGap}>
      <Group justifyContent="space-between" alignItems="center">
        <Group gap={12} alignItems="center">
          <FilterIcon fill={theme.colors.dark} width={24} height={12} />
          <Box fontFamily="gilroyBold" fontSize={dashboardSidebarFiltersTitleFontSize}>
            {filtersTitle}
          </Box>
        </Group>
        <ButtonV2
          variant="link"
          textTransform="unset"
          textDecoration="underline"
          letterSpacing={0}
          sx={{
            fontSize: 16,
            fontFamily: "gilroyRegular",
            p: 0
          }}
          onClick={onReset}
          type="button"
        >
          Reset
        </ButtonV2>
      </Group>
      {topContent}
      {contentAfterTop}
    </Stack>
  );

  const filterGroupSections = (
    <Stack gap={dashboardSidebarGap}>
      {filterGroups.map(([groupKey, values]) =>
        PILL_FILTER_GROUPS.has(groupKey) ? (
          <StaticPillFilterSection
            key={groupKey}
            groupKey={groupKey}
            values={values}
            validatedFilters={validatedFilters}
            toggleFilter={toggleFilter}
            getFilterLabel={getFilterLabel}
          />
        ) : (
          <CollapsibleFilterSection
            key={groupKey}
            groupKey={groupKey}
            values={values}
            validatedFilters={validatedFilters}
            toggleFilter={toggleFilter}
            getFilterLabel={getFilterLabel}
          />
        )
      )}
    </Stack>
  );

  if (scrollFilterGroups) {
    return (
      <Stack gap={0} flex={1} minHeight={0} width="100%">
        <Stack
          gap={dashboardSidebarGap}
          flexShrink={0}
          px={dashboardSidebarPadding}
          pt={dashboardSidebarPadding}
        >
          {headerBlock}
        </Stack>
        <Stack
          gap={dashboardSidebarGap}
          flex="1 1 auto"
          minHeight={0}
          overflowY="auto"
          px={dashboardSidebarPadding}
          pt={40}
          pb={60}
        >
          {filterGroupSections}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={dashboardSidebarGap}>
      {headerBlock}
      {filterGroupSections}
    </Stack>
  );
}

export function ProductFiltersMobile({
  allFilters,
  validatedFilters,
  toggleFilter,
  onReset,
  filtersTitle,
  topContent = null,
  contentAfterTop = null,
  getFilterLabel,
  minWidth = 310,
  maxWidth = 310,
  ...props
}) {
  const [listMenuOpen, setListMenuOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const desktopQuery = window.matchMedia(`(min-width: ${SHOP_FILTER_DESKTOP_MIN_WIDTH})`);
    const closeOnDesktop = () => {
      if (desktopQuery.matches) {
        setListMenuOpen(false);
      }
    };

    closeOnDesktop();
    desktopQuery.addEventListener("change", closeOnDesktop);
    return () => desktopQuery.removeEventListener("change", closeOnDesktop);
  }, []);

  if (!Object.keys(allFilters).length && !topContent && !contentAfterTop) {
    return null;
  }

  const { sx: propsSx, ...layoutProps } = props;

  return (
    <ProductFiltersMobileWrap {...layoutProps} {...propsSx}>
      <Menu
        opened={listMenuOpen}
        onChange={setListMenuOpen}
        placement="bottom-start"
        allowedPlacements={["bottom-start"]}
        matchReferenceWidth={false}
        disableShift
        yOffset={8}
      >
        <Menu.Target>
          <ActionIcon
            bg={listMenuOpen ? "dark" : "white"}
            variant="circle"
            size="sm"
            display="flex"
          >
            <FilterIcon fill={listMenuOpen ? "white" : theme.colors.dark} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown
          as={Stack}
          p={dashboardSidebarPadding}
          zIndex={2}
          minWidth={minWidth}
          maxWidth={maxWidth}
          borderRadius={10}
          pb={60}
        >
          <ProductFiltersStack
            allFilters={allFilters}
            validatedFilters={validatedFilters}
            toggleFilter={toggleFilter}
            onReset={() => {
              onReset();
              setListMenuOpen(false);
            }}
            filtersTitle={filtersTitle}
            topContent={topContent}
            contentAfterTop={contentAfterTop}
            getFilterLabel={getFilterLabel}
          />
        </Menu.Dropdown>
      </Menu>
    </ProductFiltersMobileWrap>
  );
}

export function ProductFiltersDesktop({
  allFilters,
  validatedFilters,
  toggleFilter,
  onReset,
  filtersTitle = "Filters",
  topContent = null,
  getFilterLabel,
  minWidth = 310,
  maxWidth = 310,
  ...props
}) {
  if (!Object.keys(allFilters).length && !topContent) {
    return null;
  }

  return (
    <ProductFiltersDesktopStack
      gap={0}
      border="1px solid black"
      borderRadius={20}
      minWidth={minWidth}
      maxWidth={maxWidth}
      alignSelf="flex-start"
      flexDirection="column"
      minHeight={0}
      data-component-name="ProductFiltersDesktop"
      {...props}
    >
      <ProductFiltersStack
        allFilters={allFilters}
        validatedFilters={validatedFilters}
        toggleFilter={toggleFilter}
        onReset={onReset}
        filtersTitle={filtersTitle}
        topContent={topContent}
        scrollFilterGroups
        getFilterLabel={getFilterLabel}
      />
    </ProductFiltersDesktopStack>
  );
}

/** @deprecated Use ProductFiltersDesktop / ProductFiltersMobile */
export default function ProductFiltersSidebar(props) {
  return <ProductFiltersDesktop {...props} />;
}
